import * as nifti from "nifti-reader-js";
import { useCallback, useEffect, useRef, useState } from "react";
import NiftiService from "../../../services/NiftiService";

/**
 * adjustDimensions:
 * Processes the NIfTI header dims to account for different storage conventions.
 */
const adjustDimensions = header => {
  const d1 = header.dims[1];
  const d2 = header.dims[2];
  const d3 = header.dims[3];

  const dimsArray = [d1, d2, d3];

  return dimsArray;
};

/**
 * Determine the primary anatomical direction of a vector
 */
const getPrimaryAnatomicalDirection = directionVector => {
  const abs = directionVector.map(Math.abs);
  const maxIndex = abs.indexOf(Math.max(...abs));

  const directions = [
    directionVector[0] > 0 ? "R" : "L", // X: +X = Right (RAS+)
    directionVector[1] > 0 ? "A" : "P", // Y: +Y = Anterior (RAS+)
    directionVector[2] > 0 ? "S" : "I", // Z: +Z = Superior (RAS+)
  ];

  return {
    direction: directions[maxIndex],
    axis: maxIndex,
    strength: abs[maxIndex],
  };
};

/**
 * Calculate orientation WITH proper plane detection
 */
const calculateNiftiOrientation = (header, modality) => {
  let affine = null;

  // Get the affine transformation matrix
  if (header.sform_code > 0 && header.affine) {
    affine = header.affine;
  } else if (
    header.qform_code > 0 &&
    header.quatern_b !== undefined &&
    header.quatern_c !== undefined &&
    header.quatern_d !== undefined
  ) {
    // Convert quaternion to rotation matrix
    const b = header.quatern_b || 0;
    const c = header.quatern_c || 0;
    const d = header.quatern_d || 0;
    const a = Math.sqrt(Math.max(0, 1.0 - (b * b + c * c + d * d)));

    const R = [
      [a * a + b * b - c * c - d * d, 2 * (b * c - a * d), 2 * (b * d + a * c)],
      [2 * (b * c + a * d), a * a + c * c - b * b - d * d, 2 * (c * d - a * b)],
      [2 * (b * d - a * c), 2 * (c * d + a * b), a * a + d * d - b * b - c * c],
    ];

    // Apply voxel size scaling and qfac
    const [dx, dy, dzRaw] = header.pixdim.slice(1, 4);
    const qfac = header.pixdim[0] || 1;
    const dz = dzRaw * qfac;

    // Build full affine matrix with translation
    affine = [
      [R[0][0] * dx, R[0][1] * dy, R[0][2] * dz, header.qoffset_x || 0],
      [R[1][0] * dx, R[1][1] * dy, R[1][2] * dz, header.qoffset_y || 0],
      [R[2][0] * dx, R[2][1] * dy, R[2][2] * dz, header.qoffset_z || 0],
      [0, 0, 0, 1],
    ];
  } else {
    const [dx, dy, dz] = header.pixdim.slice(1, 4);
    affine = [
      [dx || 1, 0, 0, 0],
      [0, dy || 1, 0, 0],
      [0, 0, dz || 1, 0],
      [0, 0, 0, 1],
    ];
  }

  // Extract direction vectors from affine columns (first 3 columns, first 3 rows)
  const xDirection = [affine[0][0], affine[1][0], affine[2][0]];
  const yDirection = [affine[0][1], affine[1][1], affine[2][1]];
  const zDirection = [affine[0][2], affine[1][2], affine[2][2]];

  const xAnatomy = getPrimaryAnatomicalDirection(xDirection);
  const yAnatomy = getPrimaryAnatomicalDirection(yDirection);
  const zAnatomy = getPrimaryAnatomicalDirection(zDirection);

  // Determine slice axis (prefer S/I direction for standard viewing)
  const axes = [
    { anatomy: xAnatomy, dim: 0, vector: xDirection },
    { anatomy: yAnatomy, dim: 1, vector: yDirection },
    { anatomy: zAnatomy, dim: 2, vector: zDirection },
  ];

  return {
    referenceSystem: [
      xAnatomy.direction,
      yAnatomy.direction,
      zAnatomy.direction,
    ],
    affine: affine,
  };
};

const getDisplayOrientation = (referenceSystem, orientation, affine) => {
  const { displayDimensions, sliceDimension } = orientation;
  const [displayDimX, displayDimY] = displayDimensions;

  // Get direction for each display axis from the reference system
  const leftToRight = referenceSystem[displayDimX]; // What direction is X axis
  const bottomToTop = referenceSystem[displayDimY]; // What direction is Y axis
  const sliceDirection = referenceSystem[sliceDimension]; // What direction is slice axis

  // Check if directions are flipped by looking at affine matrix signs
  const xFlipped = affine[displayDimX][displayDimX] < 0;
  const yFlipped = affine[displayDimY][displayDimY] < 0;
  const sliceFlipped = affine[sliceDimension][sliceDimension] < 0;

  // Map to display edges
  const opposites = { R: "L", L: "R", A: "P", P: "A", S: "I", I: "S" };

  return {
    left: xFlipped ? leftToRight : opposites[leftToRight],
    bottom: yFlipped ? bottomToTop : opposites[bottomToTop], // Account for Y flip in rendering
    slide: sliceFlipped ? sliceDirection : opposites[sliceDirection],
  };
};

const extractSliceData = (imageData, dims, sliceIndex, header, orientation) => {
  // Use orientation-aware extraction
  const { numSlices, sliceDimension, displayDimensions } = orientation;
  const [displayDimX, displayDimY] = displayDimensions;

  // Calculate slice parameters
  const totalSlices = numSlices;
  const effectiveSlice = Math.max(
    0,
    Math.min(sliceIndex || 0, totalSlices - 1)
  );

  // Extract slice based on the correct dimension
  const displayWidth = dims[displayDimX];
  const displayHeight = dims[displayDimY];
  const sliceSize = displayWidth * displayHeight;
  const extractedData = new Float32Array(sliceSize);

  const slope = header.scl_slope !== 0 ? header.scl_slope : 1;
  const intercept = header.scl_inter || 0;

  let dataIndex = 0;
  let min = Infinity,
    max = -Infinity;

  for (let j = 0; j < displayHeight; j++) {
    for (let i = 0; i < displayWidth; i++) {
      // Calculate 3D index based on slice orientation
      let idx3D = [0, 0, 0];
      idx3D[displayDimX] = i;
      idx3D[displayDimY] = j;
      idx3D[sliceDimension] = effectiveSlice;

      // Convert to linear index (NIfTI uses row-major order)
      const linearIndex =
        idx3D[2] * (dims[0] * dims[1]) + idx3D[1] * dims[0] + idx3D[0];

      if (linearIndex >= 0 && linearIndex < imageData.length) {
        const value = imageData[linearIndex] * slope + intercept;
        extractedData[dataIndex] = value;
        if (value < min) min = value;
        if (value > max) max = value;
      } else {
        // Fill with zero for out-of-bounds
        extractedData[dataIndex] = 0;
      }
      dataIndex++;
    }
  }

  if (max === min) max = min + 1;

  return {
    sliceData: extractedData,
    min,
    max,
    sliceSize,
    dimX: displayWidth,
    pixdimX: header.pixDims[displayDimX + 1] || 1,
    dimY: displayHeight,
    pixdimY: header.pixDims[displayDimY + 1] || 1,
  };
};

/**
 * processNiftiFile: Fixed parameter order and validation
 */
const processNiftiFile = (
  buffer,
  onDimRead,
  reportedDimRef,
  filename,
  setOrientations
) => {
  if (nifti.isCompressed(buffer)) {
    buffer = nifti.decompress(buffer);
  }
  if (!nifti.isNIFTI(buffer)) {
    throw new Error("Not a valid NIfTI file.");
  }

  const header = nifti.readHeader(buffer);
  const modality = NiftiService.determineModality(header, filename);
  let imageData = nifti.readImage(header, buffer);
  // Validate header dimensions
  if (!header.dims || header.dims.length < 4) {
    throw new Error("Invalid NIfTI header dimensions");
  }

  const dims = adjustDimensions(header);

  // Calculate proper orientation
  let referenceSystem;
  let affine;
  try {
    const orientation = calculateNiftiOrientation(header, modality);
    referenceSystem = orientation.referenceSystem;
    affine = orientation.affine;
  } catch (error) {
    referenceSystem = ["R", "A", "S"];
    affine = [
      [1, 0, 0, 0],
      [0, 1, 0, 0],
      [0, 0, 1, 0],
      [0, 0, 0, 1],
    ];
  }

  const singletonDims = dims.filter(dim => dim === 1).length;
  let numSlices;
  let sliceDimension = 2;
  let displayDimensions = [0, 1];

  if (singletonDims === 1) {
    numSlices = 1;
    sliceDimension = dims.indexOf(1); // The singleton dimension
    displayDimensions = [0, 1, 2].filter(i => i !== sliceDimension); // The other two
  } else {
    numSlices = Math.max(...dims);
    sliceDimension = dims.indexOf(numSlices); // Dimension with most slices
    displayDimensions = [0, 1, 2].filter(i => i !== sliceDimension); // The remaining two
  }

  if (onDimRead && reportedDimRef.current !== numSlices) {
    reportedDimRef.current = numSlices;
    onDimRead(numSlices);
  }

  const orientationInfo = {
    numSlices,
    referenceSystem,
    sliceDimension,
    displayDimensions,
  };

  // Wrap imageData in a typed array based on datatypeCode
  if (header.datatypeCode === nifti.NIFTI1.TYPE_INT16) {
    imageData = new Int16Array(imageData);
  } else if (header.datatypeCode === nifti.NIFTI1.TYPE_UINT8) {
    imageData = new Uint8Array(imageData);
  } else if (header.datatypeCode === nifti.NIFTI1.TYPE_FLOAT32) {
    imageData = new Float32Array(imageData);
  } else if (header.datatypeCode === nifti.NIFTI1.TYPE_UINT16) {
    imageData = new Uint16Array(imageData);
  } else if (header.datatypeCode === nifti.NIFTI1.TYPE_INT32) {
    imageData = new Int32Array(imageData);
  } else if (header.datatypeCode === nifti.NIFTI1.TYPE_UINT32) {
    imageData = new Uint32Array(imageData);
  } else if (header.datatypeCode === nifti.NIFTI1.TYPE_FLOAT64) {
    imageData = new Float64Array(imageData);
  } else {
    imageData = new Uint8Array(imageData);
  }

  const displayOrientation = getDisplayOrientation(
    referenceSystem,
    orientationInfo,
    affine
  );
  setOrientations(displayOrientation);

  return { header, imageData, dims, orientation: orientationInfo };
};

/**
 * drawSliceToCanvas: Fixed flickering with caching and debouncing
 */
const drawSliceToCanvas = (
  sliceData,
  min,
  max,
  sliceSize,
  dimX,
  pixdimX,
  dimY,
  pixdimY,
  container,
  canvas,
  zoom = 1,
  panOffset = { x: 0, y: 0 },
  windowLevel = 0.5,
  windowWidth = 1
) => {
  const containerRect = container.getBoundingClientRect();
  const containerWidth = Math.floor(containerRect.width);
  const containerHeight = Math.floor(containerRect.height);

  // Only resize canvas if dimensions actually changed
  if (canvas.width !== containerWidth || canvas.height !== containerHeight) {
    canvas.width = containerWidth;
    canvas.height = containerHeight;
  }

  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // Create base offscreen canvas (without windowing) - cache this
  const baseCacheKey = `${dimX}-${dimY}-${min}-${max}`;
  if (!canvas._baseCachedKey || canvas._baseCachedKey !== baseCacheKey) {
    // Create offscreen canvas for base image data
    if (!canvas._baseOffCanvas) {
      canvas._baseOffCanvas = document.createElement("canvas");
    }

    canvas._baseOffCanvas.width = dimX;
    canvas._baseOffCanvas.height = dimY;

    // Cache the raw slice data
    if (!canvas._rawSliceData || canvas._rawSliceData.length !== sliceSize) {
      canvas._rawSliceData = new Float32Array(sliceSize);
    }

    for (let i = 0; i < sliceSize; i++) {
      canvas._rawSliceData[i] = sliceData[i];
    }

    canvas._baseCachedKey = baseCacheKey;
    canvas._cachedMin = min;
    canvas._cachedMax = max;
  }

  // Apply windowing to cached data (fast operation)
  if (!canvas._offCanvas) {
    canvas._offCanvas = document.createElement("canvas");
  }

  canvas._offCanvas.width = dimX;
  canvas._offCanvas.height = dimY;
  const offCtx = canvas._offCanvas.getContext("2d");
  const imageDataCanvas = offCtx.createImageData(dimX, dimY);

  const dataRange = canvas._cachedMax - canvas._cachedMin;
  const windowCenter = canvas._cachedMin + dataRange * windowLevel;
  const windowRange = dataRange * Math.max(0.01, windowWidth);
  const windowMin = windowCenter - windowRange / 2;
  const windowMax = windowCenter + windowRange / 2;

  // Fast windowing pass
  const rawData = canvas._rawSliceData;
  for (let i = 0; i < sliceSize; i++) {
    const value = rawData[i];
    let scaled;
    if (value <= windowMin) {
      scaled = 0;
    } else if (value >= windowMax) {
      scaled = 255;
    } else {
      scaled = ((value - windowMin) / (windowMax - windowMin)) * 255;
    }

    const idx = i * 4;
    imageDataCanvas.data[idx] = scaled;
    imageDataCanvas.data[idx + 1] = scaled;
    imageDataCanvas.data[idx + 2] = scaled;
    imageDataCanvas.data[idx + 3] = 255;
  }
  offCtx.putImageData(imageDataCanvas, 0, 0);

  // Calculate aspect ratio preserving dimensions with zoom
  const imageAspect = (dimX * pixdimX) / (dimY * pixdimY);
  const containerAspect = containerWidth / containerHeight;

  let baseWidth, baseHeight;
  if (containerAspect < imageAspect) {
    baseWidth = containerWidth;
    baseHeight = containerWidth / imageAspect;
  } else {
    baseHeight = containerHeight;
    baseWidth = containerHeight * imageAspect;
  }

  // Apply zoom
  const targetWidth = baseWidth * zoom;
  const targetHeight = baseHeight * zoom;

  // Calculate base offset (centering)
  const baseOffsetX = (containerWidth - targetWidth) / 2;
  const baseOffsetY = (containerHeight - targetHeight) / 2;

  // Apply pan offset
  const offsetX = baseOffsetX + panOffset.x;
  const offsetY = baseOffsetY + panOffset.y;

  // Clear and draw
  ctx.fillStyle = "black";
  ctx.fillRect(0, 0, containerWidth, containerHeight);

  // Flip the image vertically for medical standard
  ctx.save();
  ctx.translate(offsetX, offsetY + targetHeight);
  ctx.scale(1, -1);

  ctx.drawImage(
    canvas._offCanvas,
    0,
    0,
    dimX,
    dimY,
    0,
    0,
    targetWidth,
    targetHeight
  );

  ctx.restore();
};

const NiftiViewer = ({
  file,
  sliceIndex,
  setOrientations = () => {},
  onDimRead,
  onLoadingChange = () => {},
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [parsedData, setParsedData] = useState(null);
  const reportedDimRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const renderTimeoutRef = useRef(null);

  // View state for pan, zoom, and windowing
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [windowLevel, setWindowLevel] = useState(0.5); // 0-1 range
  const [windowWidth, setWindowWidth] = useState(1); // 0-1 range
  const [isPanning, setIsPanning] = useState(false);
  const lastMousePosRef = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef(null);
  const pendingUpdateRef = useRef(null);

  // Optimized render function using requestAnimationFrame
  const debouncedRender = useCallback(() => {
    if (renderTimeoutRef.current) {
      cancelAnimationFrame(renderTimeoutRef.current);
    }

    renderTimeoutRef.current = requestAnimationFrame(() => {
      if (!parsedData) return;

      const container = containerRef.current;
      const canvas = canvasRef.current;

      if (!container || !canvas) return;

      try {
        const { header, imageData, dims, orientation } = parsedData;
        const { sliceData, min, max, sliceSize, dimX, pixdimX, dimY, pixdimY } =
          extractSliceData(imageData, dims, sliceIndex, header, orientation);

        drawSliceToCanvas(
          sliceData,
          min,
          max,
          sliceSize,
          dimX,
          pixdimX,
          dimY,
          pixdimY,
          container,
          canvas,
          zoom,
          panOffset,
          windowLevel,
          windowWidth
        );
      } catch (error) {}
    });
  }, [parsedData, sliceIndex, zoom, panOffset, windowLevel, windowWidth]);

  // Handle resize with debouncing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }

    resizeObserverRef.current = new ResizeObserver(() => {
      debouncedRender();
    });

    resizeObserverRef.current.observe(container);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (renderTimeoutRef.current) {
        cancelAnimationFrame(renderTimeoutRef.current);
      }
    };
  }, [debouncedRender]);

  // Mouse interaction handlers for windowing, zoom, and pan
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !parsedData) return;

    const handleMouseDown = event => {
      if (event.button === 0 && event.ctrlKey) {
        // Ctrl + left click = windowing
        event.preventDefault();
        setIsPanning(false);
        lastMousePosRef.current = { x: event.clientX, y: event.clientY };
        canvas.style.cursor = "crosshair";
      } else if (event.button === 0 && !event.ctrlKey) {
        // Left click = pan
        event.preventDefault();
        setIsPanning(true);
        lastMousePosRef.current = { x: event.clientX, y: event.clientY };
        canvas.style.cursor = "grab";
      }
    };

    const handleMouseMove = event => {
      if (event.buttons !== 1) return;

      const deltaX = event.clientX - lastMousePosRef.current.x;
      const deltaY = event.clientY - lastMousePosRef.current.y;

      // Store pending update instead of applying immediately
      if (!pendingUpdateRef.current) {
        pendingUpdateRef.current = {
          deltaX: 0,
          deltaY: 0,
          isPanning: isPanning && !event.ctrlKey,
          isWindowing: !isPanning && event.ctrlKey,
        };
      }

      pendingUpdateRef.current.deltaX += deltaX;
      pendingUpdateRef.current.deltaY += deltaY;

      // Throttle updates using requestAnimationFrame
      if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(() => {
          const update = pendingUpdateRef.current;
          if (!update) {
            animationFrameRef.current = null;
            return;
          }

          if (update.isPanning) {
            // Pan the image
            setPanOffset(prev => ({
              x: prev.x + update.deltaX,
              y: prev.y + update.deltaY,
            }));
          } else if (update.isWindowing) {
            // Windowing (brightness/contrast)
            const widthDelta = update.deltaX * 0.005;
            const levelDelta = -update.deltaY * 0.005;

            setWindowWidth(prev =>
              Math.max(0.01, Math.min(2, prev + widthDelta))
            );
            setWindowLevel(prev => Math.max(0, Math.min(1, prev + levelDelta)));
          }

          pendingUpdateRef.current = null;
          animationFrameRef.current = null;
        });
      }

      lastMousePosRef.current = { x: event.clientX, y: event.clientY };
    };

    const handleMouseUp = () => {
      canvas.style.cursor = "default";
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      pendingUpdateRef.current = null;
    };

    // Zoom with mouse wheel
    const handleWheel = event => {
      event.preventDefault();

      const delta = event.deltaY > 0 ? 0.9 : 1.1;
      setZoom(prev => Math.max(0.1, Math.min(10, prev * delta)));
    };

    canvas.addEventListener("mousedown", handleMouseDown);
    canvas.addEventListener("mousemove", handleMouseMove);
    canvas.addEventListener("mouseup", handleMouseUp);
    canvas.addEventListener("mouseleave", handleMouseUp);
    canvas.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      canvas.removeEventListener("mousedown", handleMouseDown);
      canvas.removeEventListener("mousemove", handleMouseMove);
      canvas.removeEventListener("mouseup", handleMouseUp);
      canvas.removeEventListener("mouseleave", handleMouseUp);
      canvas.removeEventListener("wheel", handleWheel);

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [parsedData, isPanning]);

  // Process file when it changes
  useEffect(() => {
    if (!file) {
      onLoadingChange(false);
      return;
    }

    // Reset view state when loading new file
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
    setWindowLevel(0.5);
    setWindowWidth(1);

    onLoadingChange(true);

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const result = processNiftiFile(
          e.target.result,
          onDimRead,
          reportedDimRef,
          file.name,
          setOrientations
        );
        setParsedData(result);
        onLoadingChange(false);
      } catch (error) {
        onLoadingChange(false);
      }
    };

    reader.onerror = error => {
      onLoadingChange(false);
    };

    reader.readAsArrayBuffer(file);
  }, [file, onDimRead, setOrientations, onLoadingChange]);

  // Render slice when data or slice index changes
  useEffect(() => {
    debouncedRender();
  }, [debouncedRender]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
          display: "block",
        }}
      />
    </div>
  );
};

export default NiftiViewer;
